import { themeBrandToPageContentBrand } from '@utils/pageContentBrand'

/**
 * Authored copy for Suite Availability dialog (CHP2-13348).
 * Align strings with MED-28809 / Figma where applicable.
 *
 * **Brands:** SIR, WM, CW, EBAC (and `ev` → ebac) all receive the same authored map today.
 * Brand resolution uses {@link themeBrandToPageContentBrand} (shared with page-content APIs).
 * Split per-branch objects below when CMS or product supplies distinct copy.
 *
 * Pass the **site/theme brand** from context (e.g. `useSiteContext().themeBrand`); normalization
 * is applied internally.
 */
export type SuiteAvailContent = {
  delinquency: {
    duesPastDueOsb000021: string
    duesPastDueOsb000022: string
    vipDuesPastDue: string
    genericDelinquent: string
  }
  /** API_SER_RULES_004 — AT↔WM modification; may override API message. */
  serRules004: string
  /** Scenario 2 error types — map by API code in suiteAvailErrorMapping */
  byCode: Record<string, string>
  /** When API returns a code we do not map, show API message if present, else this. */
  unknownErrorFallback: string
}

/**
 * Default authored copy for suite availability (SIR, WM, CW, and other page brands).
 * EBAC currently uses the same object; see `SUITE_AVAIL_CONTENT_EBAC`.
 */
export const SUITE_AVAIL_CONTENT_DEFAULT: SuiteAvailContent = {
  delinquency: {
    duesPastDueOsb000021: 'Your account has past due dues (OSB-000021). Please update your account to avoid restrictions.',
    duesPastDueOsb000022: 'Your account has past due dues (OSB-000022). Please update your account to avoid restrictions.',
    vipDuesPastDue: 'Your VIP account has past due dues. Please update your account to avoid restrictions.',
    genericDelinquent: 'Your account has a balance due. Please review your account status.',
  },
  serRules004: 'This location is not eligible for the selected date range. Choose a different location or a different date range to proceed.',
  byCode: {
    API_SER_RULES_004: 'This location is not eligible for the selected date range. Choose a different location or a different date range to proceed.',
    API_SER_48HR_RED_SEASON: 'A 48-hour waiting period applies for Red Season. Adjust your dates or try again later.',
    API_SER_7_NIGHT_MIN_MAX: 'A 7-night minimum and maximum stay is required for this selection.',
    API_SER_7_NIGHT_MIN_CWSP: 'A 7-night minimum stay is required (CWSP).',
    API_SER_7_NIGHT_MIN_WM: 'A 7-night minimum stay is required (WM).',
    API_SER_MAX_NIGHTS: 'Your stay exceeds the allowed maximum nights for this selection.',
    API_SER_CHECK_IN_DAY: 'Check-in must fall on an allowed check-in day for this resort.',
    API_SER_RED_WHITE_BLUE_NIGHTS: 'Equal or greater number of nights in white or blue than red season is required for this selection.',
    API_SER_MIN_NIGHT_STAY: 'Minimum night stay requirement is not met for these dates.',
    API_SER_OSA_ONLY: 'You must book OSA (Only Space Available) for this selection.',
    API_SER_MAX_UNITS: 'Nightly maximum allowed units has been reached for this selection.',
    API_SER_WEEKEND_ONLY: 'Weekend-only reservations apply for this selection.',
  },
  unknownErrorFallback: 'We could not complete this search. Try different dates or contact member services.',
}

/** Same authored map as {@link SUITE_AVAIL_CONTENT_DEFAULT}; kept for CHP2-13348 tests and imports. */
export const SUITE_AVAIL_CONTENT_EBAC: SuiteAvailContent = SUITE_AVAIL_CONTENT_DEFAULT

/** Resolves authored suite-availability copy for a site/theme brand (never null). */
export function getSuiteAvailContentForPageBrand(themeOrPageBrand: string): SuiteAvailContent {
  const pageBrand = themeBrandToPageContentBrand(themeOrPageBrand)
  switch (pageBrand) {
    case 'ebac':
      return SUITE_AVAIL_CONTENT_EBAC
    default:
      return SUITE_AVAIL_CONTENT_DEFAULT
  }
}
